import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, FileDown, CheckCircle2, MessageCircle, CalendarClock, ShieldCheck, XCircle } from "lucide-react";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { quotePublic, quoteAccept, quotePaidCheck, quoteDecline, eur, isOwnerPreview } from "../lib/quotesApi";
import { QuoteMenuBlock } from "../components/quotes/QuoteMenuBlock";

const API = process.env.REACT_APP_BACKEND_URL;
const WA_POZZO = "393513120603";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "");

export default function QuotePublicPage() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const paid = new URLSearchParams(window.location.search).get("paid") === "1";

  useEffect(() => {
    quotePublic(token).then(setQ).catch(() => setErr("Preventivo non disponibile o link non valido."));
  }, [token]);
  useEffect(() => {
    if (!paid || !q || q.status === "deposit_paid") return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      const r = await quotePaidCheck(token).catch(() => null);
      if (r?.status === "deposit_paid" || tries > 6) { clearInterval(t); if (r?.status) setQ((cur) => ({ ...cur, status: r.status })); }
    }, 1500);
    return () => clearInterval(t);
  }, [paid, q, token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await quoteAccept(token);
      if (r.checkout_url) { window.location.href = r.checkout_url; return; }
      setQ({ ...q, status: r.status });
      toast({ title: "Preventivo accettato", description: "Grazie! Ti confermiamo tutto a breve." });
    } catch (e) {
      toast({ title: "Non riuscito", description: e?.response?.data?.detail || "Riprova", variant: "destructive" });
    } finally { setBusy(false); }
  };
  const decline = async () => {
    const reason = window.prompt("Ci dispiace! Vuoi dirci il motivo? (facoltativo)", "");
    if (reason === null) return;
    try {
      await quoteDecline(token, reason);
      setQ({ ...q, status: "declined" });
    } catch (e) {
      toast({ title: "Non riuscito", description: e?.response?.data?.detail || "Riprova", variant: "destructive" });
    }
  };

  if (err) return <div className="min-h-screen bg-[#f7ebdf] grid place-items-center p-6 text-center text-[#4a1c17]" data-testid="quote-public-error">{err}</div>;
  if (!q) return <div className="min-h-screen bg-[#f7ebdf] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;

  const first = (q.customer?.name || "").split(" ")[0];
  const closed = ["accepted", "deposit_paid", "declined"].includes(q.status);
  const waText = encodeURIComponent(`Ciao! Vi scrivo per il preventivo ${q.number}${q.title ? ` (${q.title})` : ""}.`);

  return (
    <div className="min-h-screen bg-[#f7ebdf] text-[#2b1410]" data-testid="quote-public-page">
      {isOwnerPreview() && (
        <div className="bg-amber-100 text-amber-900 text-xs text-center px-4 py-2 font-semibold" data-testid="quote-owner-preview">
          Anteprima proprietario · questa apertura non viene conteggiata come "visto dal cliente"
        </div>
      )}
      <section className="relative overflow-hidden bg-[#2b1410] text-[#f7ebdf] min-h-[380px] sm:min-h-[460px] flex items-end">
        <video className="absolute inset-0 w-full h-full object-cover" src={q.hero_media} poster={q.hero_poster} autoPlay loop muted playsInline aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410] via-[#2b1410]/50 to-[#2b1410]/20" />
        <div className="relative mx-auto w-full max-w-3xl px-5 pb-8 pt-20">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#e6c46a] drop-shadow">Il Pozzo dei Giganti · Preventivo {q.number}</div>
          <h1 className="mt-3 font-serif text-3xl sm:text-5xl font-bold leading-tight" data-testid="quote-title">{q.title || (q.kind === "stay" ? "Il tuo soggiorno" : "Il tuo evento")}</h1>
          {first && <p className="mt-2 text-sm text-[#f7ebdf]/85">Preparato per {q.customer.name}</p>}
        </div>
      </section>

      <main className="mx-auto max-w-3xl px-5 -mt-6 relative pb-44 sm:pb-28 space-y-5">
        {q.status === "deposit_paid" && (
          <div className="rounded-2xl bg-emerald-600 text-white p-5 flex items-start gap-3 shadow-lg" data-testid="quote-paid-banner">
            <CheckCircle2 className="h-6 w-6 shrink-0" />
            <div><div className="font-bold">Caparra ricevuta, è tutto confermato!</div><div className="text-sm text-white/90">Grazie {first}. Vi aspettiamo al Pozzo — vi scriviamo a breve con i dettagli.</div></div>
          </div>
        )}
        {q.status === "accepted" && (
          <div className="rounded-2xl bg-white border border-emerald-300 p-5 flex items-start gap-3 shadow" data-testid="quote-accepted-banner">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
            <div><div className="font-bold">Preventivo accettato</div><div className="text-sm text-[#7c5a4e]">{q.deposit_cents ? "Se non hai completato la caparra, puoi farlo dal tasto qui sotto." : "Grazie! Ti confermiamo tutto a breve."}</div></div>
          </div>
        )}
        {q.status === "declined" && (
          <div className="rounded-2xl bg-white border border-[#e8dcc8] p-5 flex items-start gap-3 shadow" data-testid="quote-declined-banner">
            <XCircle className="h-6 w-6 shrink-0 text-[#7c5a4e]" />
            <div><div className="font-bold">Preventivo rifiutato</div><div className="text-sm text-[#7c5a4e]">Nessun problema: se cambi idea o vuoi una proposta diversa, scrivici.</div></div>
          </div>
        )}

        {q.intro && (
          <div className="rounded-2xl bg-white border border-[#e8dcc8] p-6 shadow-sm">
            <p className="font-serif text-lg leading-relaxed whitespace-pre-line" data-testid="quote-intro">{q.intro}</p>
          </div>
        )}

        <QuoteMenuBlock menu={q.menu} />

        <div className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm overflow-hidden" data-testid="quote-items">
          <div className="px-5 py-3 border-b border-[#e8dcc8] text-[11px] uppercase tracking-[0.2em] text-[#C48B31] font-bold">La proposta</div>
          <ul className="divide-y divide-[#f0e6d8]">
            {q.items.map((it) => (
              <li key={it.id} className="px-5 py-3.5 flex items-start gap-3" data-testid="quote-item">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{it.label}</div>
                  {it.note && <div className="text-xs text-[#7c5a4e] mt-0.5">{it.note}</div>}
                  <div className="text-xs text-[#7c5a4e] mt-0.5">{Number(it.qty)} {it.unit} × {eur(it.unit_cents)}</div>
                </div>
                <div className="font-semibold tabular-nums">{eur(it.total_cents)}</div>
              </li>
            ))}
          </ul>
          <div className="px-5 py-4 bg-[#faf3ee] space-y-1 text-sm">
            {q.discount_cents > 0 && <><div className="flex justify-between text-[#7c5a4e]"><span>Subtotale</span><span>{eur(q.subtotal_cents)}</span></div><div className="flex justify-between text-emerald-700"><span>Sconto</span><span>- {eur(q.discount_cents)}</span></div></>}
            <div className="flex justify-between items-baseline pt-1"><span className="font-bold text-base">Totale</span><span className="font-serif text-2xl font-bold text-[#4a1c17]" data-testid="quote-total">{eur(q.total_cents)}</span></div>
            {q.deposit_cents > 0 && <div className="flex justify-between text-[#7c5a4e]"><span>Caparra per confermare</span><span className="font-semibold text-[#2b1410]">{eur(q.deposit_cents)}</span></div>}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#e8dcc8] p-5 shadow-sm text-sm text-[#7c5a4e] space-y-2">
          <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#C48B31]" /> Valido fino al <b className="text-[#2b1410]">{fmt(q.valid_until)}</b>{q.expired && <span className="ml-2 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs">scaduto</span>}</div>
          <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#C48B31] shrink-0 mt-0.5" /><span>{q.conditions || "Prezzi IVA inclusa. Il preventivo non costituisce prenotazione fino a conferma scritta o al versamento della caparra."} <a href="/termini" className="underline" target="_blank" rel="noreferrer">Termini</a></span></div>
        </div>

        <div className="fixed bottom-0 inset-x-0 z-20 bg-[#f7ebdf]/95 backdrop-blur border-t border-[#e8dcc8]">
          <div className="mx-auto max-w-3xl px-4 py-3 flex flex-wrap items-center gap-2">
            <a href={`${API}${q.pdf_url}`} target="_blank" rel="noreferrer" className="h-11 px-4 rounded-full border border-[#4a1c17] text-[#4a1c17] inline-flex items-center gap-2 text-sm font-semibold" data-testid="quote-pdf"><FileDown className="h-4 w-4" /> PDF</a>
            <a href={`https://wa.me/${WA_POZZO}?text=${waText}`} target="_blank" rel="noreferrer" className="h-11 px-4 rounded-full bg-[#25D366] text-white inline-flex items-center gap-2 text-sm font-semibold" data-testid="quote-whatsapp"><MessageCircle className="h-4 w-4" /> {q.accept_online ? "Domande?" : "Contattaci per confermare"}</a>
            {q.accept_online && !q.expired && q.status !== "deposit_paid" && q.status !== "declined" && (
              <button onClick={accept} disabled={busy} className="h-11 px-5 rounded-full bg-[#4a1c17] text-white inline-flex items-center gap-2 text-sm font-bold ml-auto disabled:opacity-60" data-testid="quote-accept">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {q.deposit_cents ? `Accetto e pago la caparra ${eur(q.deposit_cents)}` : "Accetto il preventivo"}
              </button>
            )}
            {!closed && !q.expired && <button onClick={decline} className="text-xs text-[#7c5a4e] underline ml-auto sm:ml-2" data-testid="quote-decline">Non fa per me</button>}
          </div>
        </div>
      </main>
      <Toaster />
    </div>
  );
}
